import React, { useState, useRef, useEffect } from 'react';
import '../styles/WallpaperPicker.css';

const WallpaperPicker = ({ isOpen, onClose, onSelectWallpaper, currentWallpaper }) => {
  const fileInputRef = useRef(null);
  const [selectedWallpaper, setSelectedWallpaper] = useState(currentWallpaper?.id || 'default');
  const [displaySettings, setDisplaySettings] = useState(
    currentWallpaper?.displaySettings || {
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center'
    }
  );

  // Update state when currentWallpaper changes
  useEffect(() => {
    if (currentWallpaper) {
      setSelectedWallpaper(currentWallpaper.id || 'default');
      setDisplaySettings(currentWallpaper.displaySettings || {
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center'
      });
    }
  }, [currentWallpaper]);

  // SVG Pattern generator functions
  const generateChatPatternSVG = (bgColor, iconColor) => {
    const svg = `
      <svg width="280" height="280" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="chatPattern" x="0" y="0" width="280" height="280" patternUnits="userSpaceOnUse">
            <rect width="280" height="280" fill="${bgColor}"/>
            <g stroke="${iconColor}" stroke-width="2" fill="none" opacity="0.7" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="50" cy="50" r="18"/>
              <path d="M41 59 L35 65"/>
              <rect x="180" y="30" width="25" height="40" rx="3"/>
              <line x1="185" y1="35" x2="200" y2="35"/>
              <circle cx="192.5" cy="65" r="2"/>
              <rect x="100" y="150" width="40" height="25" rx="2"/>
              <path d="M100 150 L120 162 L140 150"/>
              <path d="M230 180 Q230 160 220 160 Q210 160 210 170 Q210 180 220 190 L220 200"/>
              <circle cx="220" cy="175" r="5"/>
              <path d="M40 200 Q30 190 30 180 Q30 170 40 170 Q50 170 50 180 Q50 190 40 200 L40 210"/>
              <circle cx="8" cy="120" r="3"/>
              <circle cx="260" cy="90" r="3"/>
              <circle cx="150" cy="240" r="3"/>
            </g>
          </pattern>
        </defs>
        <rect width="280" height="280" fill="url(#chatPattern)"/>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const generateDotsPatternSVG = (bgColor, dotColor) => {
    const svg = `
      <svg width="60" height="60" xmlns="http://www.w3.org/2000/svg">
        <rect width="60" height="60" fill="${bgColor}"/>
        <circle cx="10" cy="10" r="2" fill="${dotColor}" opacity="0.4"/>
        <circle cx="30" cy="30" r="2" fill="${dotColor}" opacity="0.4"/>
        <circle cx="50" cy="50" r="2" fill="${dotColor}" opacity="0.4"/>
        <circle cx="50" cy="10" r="2" fill="${dotColor}" opacity="0.4"/>
        <circle cx="10" cy="50" r="2" fill="${dotColor}" opacity="0.4"/>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const generateHeartsPatternSVG = (bgColor, heartColor) => {
    const svg = `
      <svg width="120" height="120" xmlns="http://www.w3.org/2000/svg">
        <rect width="120" height="120" fill="${bgColor}"/>
        <g fill="none" stroke="${heartColor}" stroke-width="1.5" opacity="0.3">
          <path d="M30 35 Q30 25 35 25 Q40 25 40 30 Q40 25 45 25 Q50 25 50 35 Q50 45 40 50 Q30 45 30 35"/>
          <path d="M80 85 Q80 75 85 75 Q90 75 90 80 Q90 75 95 75 Q100 75 100 85 Q100 95 90 100 Q80 95 80 85"/>
        </g>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const generateGeometricPatternSVG = (bgColor, lineColor) => {
    const svg = `
      <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
        <rect width="100" height="100" fill="${bgColor}"/>
        <g stroke="${lineColor}" stroke-width="1" fill="none" opacity="0.3">
          <circle cx="50" cy="50" r="30"/>
          <rect x="20" y="20" width="60" height="60" rx="5"/>
          <line x1="0" y1="0" x2="100" y2="100"/>
          <line x1="100" y1="0" x2="0" y2="100"/>
        </g>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const generateBubblesPatternSVG = (bgColor, bubbleColor) => {
    const svg = `
      <svg width="150" height="150" xmlns="http://www.w3.org/2000/svg">
        <rect width="150" height="150" fill="${bgColor}"/>
        <g fill="none" stroke="${bubbleColor}" stroke-width="2" opacity="0.4">
          <circle cx="30" cy="30" r="20"/>
          <circle cx="100" cy="50" r="15"/>
          <circle cx="60" cy="100" r="25"/>
          <circle cx="120" cy="120" r="18"/>
        </g>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const generateWavesPatternSVG = (bgColor, waveColor) => {
    const svg = `
      <svg width="200" height="100" xmlns="http://www.w3.org/2000/svg">
        <rect width="200" height="100" fill="${bgColor}"/>
        <g fill="none" stroke="${waveColor}" stroke-width="2" opacity="0.3">
          <path d="M0 30 Q50 10 100 30 T200 30"/>
          <path d="M0 50 Q50 30 100 50 T200 50"/>
          <path d="M0 70 Q50 50 100 70 T200 70"/>
        </g>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  // Animated Dark Patterns
  const generateAnimatedStarsSVG = () => {
    const svg = `
      <svg width="400" height="400" xmlns="http://www.w3.org/2000/svg">
        <rect width="400" height="400" fill="#000000"/>
        <g>
          <circle cx="50" cy="50" r="2" fill="#ffffff" opacity="0.8">
            <animate attributeName="opacity" values="0.3;1;0.3" dur="3s" repeatCount="indefinite"/>
          </circle>
          <circle cx="150" cy="80" r="1.5" fill="#ffffff" opacity="0.6">
            <animate attributeName="opacity" values="0.2;0.8;0.2" dur="4s" repeatCount="indefinite"/>
          </circle>
          <circle cx="280" cy="120" r="2.5" fill="#ffffff" opacity="0.9">
            <animate attributeName="opacity" values="0.4;1;0.4" dur="2.5s" repeatCount="indefinite"/>
          </circle>
          <circle cx="350" cy="60" r="1" fill="#ffffff" opacity="0.5">
            <animate attributeName="opacity" values="0.2;0.7;0.2" dur="3.5s" repeatCount="indefinite"/>
          </circle>
          <circle cx="100" cy="200" r="2" fill="#ffffff" opacity="0.7">
            <animate attributeName="opacity" values="0.3;0.9;0.3" dur="4.5s" repeatCount="indefinite"/>
          </circle>
          <circle cx="250" cy="250" r="1.5" fill="#ffffff" opacity="0.6">
            <animate attributeName="opacity" values="0.2;0.8;0.2" dur="3s" repeatCount="indefinite"/>
          </circle>
          <circle cx="380" cy="300" r="2" fill="#ffffff" opacity="0.8">
            <animate attributeName="opacity" values="0.4;1;0.4" dur="2s" repeatCount="indefinite"/>
          </circle>
          <circle cx="80" cy="350" r="1" fill="#ffffff" opacity="0.5">
            <animate attributeName="opacity" values="0.2;0.6;0.2" dur="5s" repeatCount="indefinite"/>
          </circle>
          <circle cx="200" cy="180" r="2.5" fill="#ffffff" opacity="0.9">
            <animate attributeName="opacity" values="0.5;1;0.5" dur="3.5s" repeatCount="indefinite"/>
          </circle>
          <circle cx="320" cy="380" r="1.5" fill="#ffffff" opacity="0.7">
            <animate attributeName="opacity" values="0.3;0.8;0.3" dur="4s" repeatCount="indefinite"/>
          </circle>
        </g>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const generateAnimatedParticlesSVG = () => {
    const svg = `
      <svg width="400" height="400" xmlns="http://www.w3.org/2000/svg">
        <rect width="400" height="400" fill="#0a0a0a"/>
        <g>
          <circle cx="100" cy="100" r="3" fill="#4a9eff" opacity="0.6">
            <animate attributeName="cy" values="100;50;100" dur="6s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.3;0.8;0.3" dur="6s" repeatCount="indefinite"/>
          </circle>
          <circle cx="200" cy="150" r="2.5" fill="#7b68ee" opacity="0.5">
            <animate attributeName="cy" values="150;100;150" dur="7s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.2;0.7;0.2" dur="7s" repeatCount="indefinite"/>
          </circle>
          <circle cx="300" cy="200" r="3.5" fill="#00d4ff" opacity="0.7">
            <animate attributeName="cy" values="200;140;200" dur="5s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.4;0.9;0.4" dur="5s" repeatCount="indefinite"/>
          </circle>
          <circle cx="150" cy="300" r="2" fill="#9d4edd" opacity="0.5">
            <animate attributeName="cy" values="300;250;300" dur="8s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.2;0.6;0.2" dur="8s" repeatCount="indefinite"/>
          </circle>
          <circle cx="350" cy="350" r="3" fill="#5390d9" opacity="0.6">
            <animate attributeName="cy" values="350;290;350" dur="6.5s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.3;0.8;0.3" dur="6.5s" repeatCount="indefinite"/>
          </circle>
          <circle cx="50" cy="250" r="2.5" fill="#48bfe3" opacity="0.5">
            <animate attributeName="cy" values="250;200;250" dur="7.5s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.2;0.7;0.2" dur="7.5s" repeatCount="indefinite"/>
          </circle>
        </g>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const generateAnimatedGlowSVG = () => {
    const svg = `
      <svg width="400" height="400" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="glow1">
            <stop offset="0%" stop-color="#ff006e" stop-opacity="0.8"/>
            <stop offset="100%" stop-color="#ff006e" stop-opacity="0"/>
          </radialGradient>
          <radialGradient id="glow2">
            <stop offset="0%" stop-color="#8338ec" stop-opacity="0.8"/>
            <stop offset="100%" stop-color="#8338ec" stop-opacity="0"/>
          </radialGradient>
          <radialGradient id="glow3">
            <stop offset="0%" stop-color="#3a86ff" stop-opacity="0.8"/>
            <stop offset="100%" stop-color="#3a86ff" stop-opacity="0"/>
          </radialGradient>
        </defs>
        <rect width="400" height="400" fill="#000000"/>
        <circle cx="100" cy="100" r="40" fill="url(#glow1)">
          <animate attributeName="r" values="30;50;30" dur="4s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.3;0.7;0.3" dur="4s" repeatCount="indefinite"/>
        </circle>
        <circle cx="300" cy="150" r="35" fill="url(#glow2)">
          <animate attributeName="r" values="25;45;25" dur="5s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.2;0.6;0.2" dur="5s" repeatCount="indefinite"/>
        </circle>
        <circle cx="200" cy="300" r="45" fill="url(#glow3)">
          <animate attributeName="r" values="35;55;35" dur="6s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.3;0.8;0.3" dur="6s" repeatCount="indefinite"/>
        </circle>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const generateAnimatedWavesSVG = () => {
    const svg = `
      <svg width="400" height="400" xmlns="http://www.w3.org/2000/svg">
        <rect width="400" height="400" fill="#0d1117"/>
        <g fill="none" stroke="#58a6ff" stroke-width="2" opacity="0.4">
          <path d="M0 100 Q100 80 200 100 T400 100">
            <animate attributeName="d" values="M0 100 Q100 80 200 100 T400 100;M0 100 Q100 120 200 100 T400 100;M0 100 Q100 80 200 100 T400 100" dur="4s" repeatCount="indefinite"/>
          </path>
          <path d="M0 200 Q100 180 200 200 T400 200">
            <animate attributeName="d" values="M0 200 Q100 180 200 200 T400 200;M0 200 Q100 220 200 200 T400 200;M0 200 Q100 180 200 200 T400 200" dur="5s" repeatCount="indefinite"/>
          </path>
          <path d="M0 300 Q100 280 200 300 T400 300">
            <animate attributeName="d" values="M0 300 Q100 280 200 300 T400 300;M0 300 Q100 320 200 300 T400 300;M0 300 Q100 280 200 300 T400 300" dur="6s" repeatCount="indefinite"/>
          </path>
        </g>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const generateAnimatedMatrixSVG = () => {
    const svg = `
      <svg width="400" height="400" xmlns="http://www.w3.org/2000/svg">
        <rect width="400" height="400" fill="#000000"/>
        <g fill="#00ff41" opacity="0.6" font-family="monospace" font-size="14">
          <text x="50" y="0">
            01001
            <animate attributeName="y" values="0;400" dur="8s" repeatCount="indefinite"/>
          </text>
          <text x="100" y="0">
            11010
            <animate attributeName="y" values="0;400" dur="10s" repeatCount="indefinite"/>
          </text>
          <text x="150" y="0">
            10110
            <animate attributeName="y" values="0;400" dur="7s" repeatCount="indefinite"/>
          </text>
          <text x="200" y="0">
            01101
            <animate attributeName="y" values="0;400" dur="9s" repeatCount="indefinite"/>
          </text>
          <text x="250" y="0">
            11001
            <animate attributeName="y" values="0;400" dur="11s" repeatCount="indefinite"/>
          </text>
          <text x="300" y="0">
            10011
            <animate attributeName="y" values="0;400" dur="6s" repeatCount="indefinite"/>
          </text>
          <text x="350" y="0">
            01110
            <animate attributeName="y" values="0;400" dur="8.5s" repeatCount="indefinite"/>
          </text>
        </g>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  // Light Beam Effects
  const generateLightBeamsSVG = (color = 'blue') => {
    const colors = {
      blue: { beam: '#4a9eff', glow: 'rgba(74, 158, 255, 0.3)' },
      purple: { beam: '#a855f7', glow: 'rgba(168, 85, 247, 0.3)' },
      cyan: { beam: '#06b6d4', glow: 'rgba(6, 182, 212, 0.3)' },
      gold: { beam: '#fbbf24', glow: 'rgba(251, 191, 36, 0.3)' }
    };
    
    const svg = `
      <svg width="400" height="400" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="beam1-${color}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="${colors[color].beam}" stop-opacity="0"/>
            <stop offset="50%" stop-color="${colors[color].beam}" stop-opacity="0.6"/>
            <stop offset="100%" stop-color="${colors[color].beam}" stop-opacity="0"/>
          </linearGradient>
          <linearGradient id="beam2-${color}" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="${colors[color].beam}" stop-opacity="0"/>
            <stop offset="50%" stop-color="${colors[color].beam}" stop-opacity="0.4"/>
            <stop offset="100%" stop-color="${colors[color].beam}" stop-opacity="0"/>
          </linearGradient>
        </defs>
        <rect width="400" height="400" fill="#000000"/>
        
        <!-- Animated light beams -->
        <rect x="50" y="0" width="30" height="400" fill="url(#beam1-${color})" opacity="0.5">
          <animate attributeName="opacity" values="0.3;0.8;0.3" dur="3s" repeatCount="indefinite"/>
        </rect>
        <rect x="150" y="0" width="20" height="400" fill="url(#beam2-${color})" opacity="0.4">
          <animate attributeName="opacity" values="0.2;0.6;0.2" dur="4s" repeatCount="indefinite"/>
        </rect>
        <rect x="250" y="0" width="35" height="400" fill="url(#beam1-${color})" opacity="0.6">
          <animate attributeName="opacity" values="0.4;0.9;0.4" dur="3.5s" repeatCount="indefinite"/>
        </rect>
        <rect x="330" y="0" width="25" height="400" fill="url(#beam2-${color})" opacity="0.5">
          <animate attributeName="opacity" values="0.3;0.7;0.3" dur="4.5s" repeatCount="indefinite"/>
        </rect>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const generateSpotlightSVG = (color = 'white') => {
    const colors = {
      white: 'rgba(255, 255, 255, 0.8)',
      blue: 'rgba(74, 158, 255, 0.8)',
      purple: 'rgba(168, 85, 247, 0.8)',
      cyan: 'rgba(6, 182, 212, 0.8)'
    };
    
    const svg = `
      <svg width="400" height="400" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="spotlight-${color}">
            <stop offset="0%" stop-color="${colors[color]}" stop-opacity="0.8"/>
            <stop offset="50%" stop-color="${colors[color]}" stop-opacity="0.3"/>
            <stop offset="100%" stop-color="${colors[color]}" stop-opacity="0"/>
          </radialGradient>
        </defs>
        <rect width="400" height="400" fill="#000000"/>
        
        <!-- Animated spotlights -->
        <circle cx="100" cy="100" r="80" fill="url(#spotlight-${color})">
          <animate attributeName="r" values="60;100;60" dur="4s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.4;0.8;0.4" dur="4s" repeatCount="indefinite"/>
        </circle>
        <circle cx="300" cy="200" r="100" fill="url(#spotlight-${color})">
          <animate attributeName="r" values="80;120;80" dur="5s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.3;0.7;0.3" dur="5s" repeatCount="indefinite"/>
        </circle>
        <circle cx="150" cy="320" r="90" fill="url(#spotlight-${color})">
          <animate attributeName="r" values="70;110;70" dur="4.5s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.4;0.9;0.4" dur="4.5s" repeatCount="indefinite"/>
        </circle>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const generateAuroraSVG = () => {
    const svg = `
      <svg width="400" height="400" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="aurora1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#4a9eff" stop-opacity="0.6"/>
            <stop offset="50%" stop-color="#a855f7" stop-opacity="0.4"/>
            <stop offset="100%" stop-color="#06b6d4" stop-opacity="0.6"/>
          </linearGradient>
          <linearGradient id="aurora2" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#06b6d4" stop-opacity="0.5"/>
            <stop offset="50%" stop-color="#4a9eff" stop-opacity="0.3"/>
            <stop offset="100%" stop-color="#a855f7" stop-opacity="0.5"/>
          </linearGradient>
        </defs>
        <rect width="400" height="400" fill="#000814"/>
        
        <!-- Aurora waves -->
        <path d="M0 100 Q100 80 200 100 T400 100 L400 200 Q300 180 200 200 T0 200 Z" fill="url(#aurora1)">
          <animate attributeName="d" 
            values="M0 100 Q100 80 200 100 T400 100 L400 200 Q300 180 200 200 T0 200 Z;
                    M0 100 Q100 120 200 100 T400 100 L400 200 Q300 220 200 200 T0 200 Z;
                    M0 100 Q100 80 200 100 T400 100 L400 200 Q300 180 200 200 T0 200 Z"
            dur="6s" repeatCount="indefinite"/>
        </path>
        <path d="M0 250 Q100 230 200 250 T400 250 L400 350 Q300 330 200 350 T0 350 Z" fill="url(#aurora2)">
          <animate attributeName="d" 
            values="M0 250 Q100 230 200 250 T400 250 L400 350 Q300 330 200 350 T0 350 Z;
                    M0 250 Q100 270 200 250 T400 250 L400 350 Q300 370 200 350 T0 350 Z;
                    M0 250 Q100 230 200 250 T400 250 L400 350 Q300 330 200 350 T0 350 Z"
            dur="7s" repeatCount="indefinite"/>
        </path>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  // Pulsing Ring Effects
  const generatePulsingRingsSVG = (color = 'pink') => {
    const colors = {
      pink: '#ff006e',
      blue: '#4a9eff',
      purple: '#a855f7',
      cyan: '#06b6d4',
      green: '#10b981'
    };
    
    const svg = `
      <svg width="400" height="400" xmlns="http://www.w3.org/2000/svg">
        <rect width="400" height="400" fill="#000000"/>
        
        <!-- Center pulsing rings -->
        <g transform="translate(200, 200)">
          <circle cx="0" cy="0" r="30" fill="none" stroke="${colors[color]}" stroke-width="2" opacity="0.8">
            <animate attributeName="r" values="30;80;30" dur="3s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.8;0;0.8" dur="3s" repeatCount="indefinite"/>
          </circle>
          <circle cx="0" cy="0" r="30" fill="none" stroke="${colors[color]}" stroke-width="2" opacity="0.8">
            <animate attributeName="r" values="30;80;30" dur="3s" begin="1s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.8;0;0.8" dur="3s" begin="1s" repeatCount="indefinite"/>
          </circle>
          <circle cx="0" cy="0" r="30" fill="none" stroke="${colors[color]}" stroke-width="2" opacity="0.8">
            <animate attributeName="r" values="30;80;30" dur="3s" begin="2s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.8;0;0.8" dur="3s" begin="2s" repeatCount="indefinite"/>
          </circle>
        </g>
        
        <!-- Corner rings -->
        <g transform="translate(80, 80)">
          <circle cx="0" cy="0" r="20" fill="none" stroke="${colors[color]}" stroke-width="1.5" opacity="0.6">
            <animate attributeName="r" values="20;50;20" dur="4s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.6;0;0.6" dur="4s" repeatCount="indefinite"/>
          </circle>
        </g>
        <g transform="translate(320, 320)">
          <circle cx="0" cy="0" r="20" fill="none" stroke="${colors[color]}" stroke-width="1.5" opacity="0.6">
            <animate attributeName="r" values="20;50;20" dur="4s" begin="1.5s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.6;0;0.6" dur="4s" begin="1.5s" repeatCount="indefinite"/>
          </circle>
        </g>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const generateDualColorRingsSVG = () => {
    const svg = `
      <svg width="400" height="400" xmlns="http://www.w3.org/2000/svg">
        <rect width="400" height="400" fill="#000000"/>
        
        <!-- Pink rings -->
        <g transform="translate(150, 200)">
          <circle cx="0" cy="0" r="30" fill="none" stroke="#ff006e" stroke-width="2" opacity="0.7">
            <animate attributeName="r" values="30;90;30" dur="4s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.7;0;0.7" dur="4s" repeatCount="indefinite"/>
          </circle>
          <circle cx="0" cy="0" r="30" fill="none" stroke="#ff006e" stroke-width="2" opacity="0.7">
            <animate attributeName="r" values="30;90;30" dur="4s" begin="2s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.7;0;0.7" dur="4s" begin="2s" repeatCount="indefinite"/>
          </circle>
        </g>
        
        <!-- Blue rings -->
        <g transform="translate(250, 200)">
          <circle cx="0" cy="0" r="30" fill="none" stroke="#4a9eff" stroke-width="2" opacity="0.7">
            <animate attributeName="r" values="30;90;30" dur="4s" begin="1s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.7;0;0.7" dur="4s" begin="1s" repeatCount="indefinite"/>
          </circle>
          <circle cx="0" cy="0" r="30" fill="none" stroke="#4a9eff" stroke-width="2" opacity="0.7">
            <animate attributeName="r" values="30;90;30" dur="4s" begin="3s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.7;0;0.7" dur="4s" begin="3s" repeatCount="indefinite"/>
          </circle>
        </g>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const generateMultiRingsSVG = () => {
    const svg = `
      <svg width="400" height="400" xmlns="http://www.w3.org/2000/svg">
        <rect width="400" height="400" fill="#0a0a0a"/>
        
        <!-- Multiple colored rings -->
        <g transform="translate(100, 100)">
          <circle cx="0" cy="0" r="25" fill="none" stroke="#ff006e" stroke-width="2" opacity="0.6">
            <animate attributeName="r" values="25;70;25" dur="3.5s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.6;0;0.6" dur="3.5s" repeatCount="indefinite"/>
          </circle>
        </g>
        <g transform="translate(300, 100)">
          <circle cx="0" cy="0" r="25" fill="none" stroke="#4a9eff" stroke-width="2" opacity="0.6">
            <animate attributeName="r" values="25;70;25" dur="3.5s" begin="1s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.6;0;0.6" dur="3.5s" begin="1s" repeatCount="indefinite"/>
          </circle>
        </g>
        <g transform="translate(200, 250)">
          <circle cx="0" cy="0" r="25" fill="none" stroke="#a855f7" stroke-width="2" opacity="0.6">
            <animate attributeName="r" values="25;70;25" dur="3.5s" begin="0.5s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.6;0;0.6" dur="3.5s" begin="0.5s" repeatCount="indefinite"/>
          </circle>
        </g>
        <g transform="translate(100, 300)">
          <circle cx="0" cy="0" r="25" fill="none" stroke="#06b6d4" stroke-width="2" opacity="0.6">
            <animate attributeName="r" values="25;70;25" dur="3.5s" begin="1.5s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.6;0;0.6" dur="3.5s" begin="1.5s" repeatCount="indefinite"/>
          </circle>
        </g>
        <g transform="translate(300, 300)">
          <circle cx="0" cy="0" r="25" fill="none" stroke="#10b981" stroke-width="2" opacity="0.6">
            <animate attributeName="r" values="25;70;25" dur="3.5s" begin="2s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.6;0;0.6" dur="3.5s" begin="2s" repeatCount="indefinite"/>
          </circle>
        </g>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  // Canvas-based effect generators with color variations
  const generateCanvasParticlesNetwork = (color = 'blue') => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 400;
    const ctx = canvas.getContext('2d');
    
    const colors = {
      blue: { particle: '#4a9eff', line: 'rgba(74, 158, 255, 0.2)' },
      red: { particle: '#ff4a6e', line: 'rgba(255, 74, 110, 0.2)' },
      white: { particle: '#ffffff', line: 'rgba(255, 255, 255, 0.2)' }
    };
    
    // Background
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, 400, 400);
    
    // Particles
    const particles = [];
    for (let i = 0; i < 50; i++) {
      particles.push({
        x: Math.random() * 400,
        y: Math.random() * 400,
        size: Math.random() * 2 + 1
      });
    }
    
    // Draw particles
    particles.forEach(p => {
      ctx.fillStyle = colors[color].particle;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    });
    
    // Draw connections
    ctx.strokeStyle = colors[color].line;
    ctx.lineWidth = 0.5;
    particles.forEach((p1, i) => {
      particles.slice(i + 1).forEach(p2 => {
        const dist = Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
        if (dist < 100) {
          ctx.beginPath();
          ctx.moveTo(p1.x, p1.y);
          ctx.lineTo(p2.x, p2.y);
          ctx.stroke();
        }
      });
    });
    
    return canvas.toDataURL();
  };

  const generateCanvasConstellation = (color = 'white') => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 400;
    const ctx = canvas.getContext('2d');
    
    const colors = {
      white: { star: '255, 255, 255', line: 'rgba(255, 255, 255, 0.15)' },
      blue: { star: '74, 158, 255', line: 'rgba(74, 158, 255, 0.15)' },
      red: { star: '255, 74, 110', line: 'rgba(255, 74, 110, 0.15)' }
    };
    
    // Background
    ctx.fillStyle = '#000814';
    ctx.fillRect(0, 0, 400, 400);
    
    // Stars
    const stars = [];
    for (let i = 0; i < 80; i++) {
      stars.push({
        x: Math.random() * 400,
        y: Math.random() * 400,
        size: Math.random() * 1.5 + 0.5,
        brightness: Math.random()
      });
    }
    
    // Draw stars
    stars.forEach(s => {
      ctx.fillStyle = `rgba(${colors[color].star}, ${s.brightness})`;
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
      ctx.fill();
    });
    
    // Draw constellation lines
    ctx.strokeStyle = colors[color].line;
    ctx.lineWidth = 1;
    for (let i = 0; i < 15; i++) {
      const s1 = stars[Math.floor(Math.random() * stars.length)];
      const s2 = stars[Math.floor(Math.random() * stars.length)];
      ctx.beginPath();
      ctx.moveTo(s1.x, s1.y);
      ctx.lineTo(s2.x, s2.y);
      ctx.stroke();
    }
    
    return canvas.toDataURL();
  };

  const generateCanvasHexagons = (color = 'blue') => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 400;
    const ctx = canvas.getContext('2d');
    
    const colors = {
      blue: 'rgba(74, 158, 255, 0.3)',
      red: 'rgba(255, 74, 110, 0.3)',
      white: 'rgba(255, 255, 255, 0.3)'
    };
    
    // Background
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, 400, 400);
    
    // Hexagon function
    const drawHexagon = (x, y, size) => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i;
        const hx = x + size * Math.cos(angle);
        const hy = y + size * Math.sin(angle);
        if (i === 0) ctx.moveTo(hx, hy);
        else ctx.lineTo(hx, hy);
      }
      ctx.closePath();
    };
    
    // Draw hexagons
    const size = 30;
    const spacing = size * 1.8;
    ctx.strokeStyle = colors[color];
    ctx.lineWidth = 1.5;
    
    for (let y = 0; y < 400 + spacing; y += spacing * 0.866) {
      for (let x = 0; x < 400 + spacing; x += spacing) {
        const offsetX = (y / (spacing * 0.866)) % 2 === 0 ? 0 : spacing / 2;
        drawHexagon(x + offsetX, y, size);
        ctx.stroke();
      }
    }
    
    return canvas.toDataURL();
  };

  const generateCanvasCircuitBoard = (color = 'blue') => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 400;
    const ctx = canvas.getContext('2d');
    
    const colors = {
      blue: { line: 'rgba(74, 158, 255, 0.3)', node: 'rgba(74, 158, 255, 0.5)' },
      red: { line: 'rgba(255, 74, 110, 0.3)', node: 'rgba(255, 74, 110, 0.5)' },
      white: { line: 'rgba(255, 255, 255, 0.3)', node: 'rgba(255, 255, 255, 0.5)' }
    };
    
    // Background
    ctx.fillStyle = '#0d1b2a';
    ctx.fillRect(0, 0, 400, 400);
    
    // Circuit lines
    ctx.strokeStyle = colors[color].line;
    ctx.lineWidth = 2;
    
    // Horizontal lines
    for (let i = 0; i < 10; i++) {
      const y = Math.random() * 400;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(400, y);
      ctx.stroke();
    }
    
    // Vertical lines
    for (let i = 0; i < 10; i++) {
      const x = Math.random() * 400;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, 400);
      ctx.stroke();
    }
    
    // Circuit nodes
    ctx.fillStyle = colors[color].node;
    for (let i = 0; i < 30; i++) {
      const x = Math.random() * 400;
      const y = Math.random() * 400;
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fill();
    }
    
    return canvas.toDataURL();
  };

  const generateCanvasNeonGrid = (color = 'blue') => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 400;
    const ctx = canvas.getContext('2d');
    
    const colors = {
      blue: { grid: 'rgba(74, 158, 255, 0.3)', glow: 'rgba(74, 158, 255, 0.6)' },
      red: { grid: 'rgba(255, 74, 110, 0.3)', glow: 'rgba(255, 74, 110, 0.6)' },
      white: { grid: 'rgba(255, 255, 255, 0.3)', glow: 'rgba(255, 255, 255, 0.6)' }
    };
    
    // Background
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, 400, 400);
    
    // Grid
    const gridSize = 40;
    ctx.strokeStyle = colors[color].grid;
    ctx.lineWidth = 1;
    
    // Vertical lines
    for (let x = 0; x <= 400; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, 400);
      ctx.stroke();
    }
    
    // Horizontal lines
    for (let y = 0; y <= 400; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(400, y);
      ctx.stroke();
    }
    
    // Neon glow effect
    ctx.strokeStyle = colors[color].glow;
    ctx.lineWidth = 2;
    for (let i = 0; i < 5; i++) {
      const x1 = Math.floor(Math.random() * 10) * gridSize;
      const y1 = Math.floor(Math.random() * 10) * gridSize;
      const x2 = Math.floor(Math.random() * 10) * gridSize;
      const y2 = Math.floor(Math.random() * 10) * gridSize;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    }
    
    return canvas.toDataURL();
  };

  // Accurate WhatsApp-style patterns
  const generateWhatsAppClassicPattern = () => {
    const svg = `
      <svg width="450" height="450" xmlns="http://www.w3.org/2000/svg">
        <rect width="450" height="450" fill="#e5ddd5"/>
        <g opacity="0.06" fill="none" stroke="#000000" stroke-width="1.2" stroke-linecap="round">
          <!-- Row 1 -->
          <g transform="translate(60, 60)">
            <circle cx="0" cy="0" r="25"/>
            <path d="M-15 12 L-22 22"/>
          </g>
          <g transform="translate(225, 60)">
            <rect x="-15" y="-20" width="30" height="40" rx="4"/>
            <line x1="-10" y1="-15" x2="10" y2="-15"/>
            <circle cx="0" cy="15" r="2.5"/>
          </g>
          <g transform="translate(390, 60)">
            <rect x="-20" y="-12" width="40" height="24" rx="2"/>
            <path d="M-20 -12 L0 0 L20 -12"/>
          </g>
          
          <!-- Row 2 -->
          <g transform="translate(60, 225)">
            <path d="M0 -15 Q-12 -20 -15 -10 Q-18 0 -10 8 Q0 15 0 15"/>
            <circle cx="-10" cy="-5" r="4"/>
          </g>
          <g transform="translate(225, 225)">
            <path d="M-10 -10 Q-10 -20 0 -20 Q10 -20 10 -10 Q10 0 0 10 Q-10 0 -10 -10"/>
          </g>
          <g transform="translate(390, 225)">
            <circle cx="0" cy="0" r="25"/>
            <path d="M-15 12 L-22 22"/>
          </g>
          
          <!-- Row 3 -->
          <g transform="translate(60, 390)">
            <rect x="-20" y="-12" width="40" height="24" rx="2"/>
            <path d="M-20 -12 L0 0 L20 -12"/>
          </g>
          <g transform="translate(225, 390)">
            <circle cx="0" cy="0" r="25"/>
            <path d="M15 12 L22 22"/>
          </g>
          <g transform="translate(390, 390)">
            <rect x="-15" y="-20" width="30" height="40" rx="4"/>
            <line x1="-10" y1="-15" x2="10" y2="-15"/>
            <circle cx="0" cy="15" r="2.5"/>
          </g>
        </g>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const generateWhatsAppDarkPattern = () => {
    const svg = `
      <svg width="450" height="450" xmlns="http://www.w3.org/2000/svg">
        <rect width="450" height="450" fill="#0b141a"/>
        <g opacity="0.08" fill="none" stroke="#25d366" stroke-width="1.2" stroke-linecap="round">
          <!-- Row 1 -->
          <g transform="translate(60, 60)">
            <circle cx="0" cy="0" r="25"/>
            <path d="M-15 12 L-22 22"/>
          </g>
          <g transform="translate(225, 60)">
            <rect x="-15" y="-20" width="30" height="40" rx="4"/>
            <line x1="-10" y1="-15" x2="10" y2="-15"/>
            <circle cx="0" cy="15" r="2.5"/>
          </g>
          <g transform="translate(390, 60)">
            <rect x="-20" y="-12" width="40" height="24" rx="2"/>
            <path d="M-20 -12 L0 0 L20 -12"/>
          </g>
          
          <!-- Row 2 -->
          <g transform="translate(60, 225)">
            <path d="M0 -15 Q-12 -20 -15 -10 Q-18 0 -10 8 Q0 15 0 15"/>
            <circle cx="-10" cy="-5" r="4"/>
          </g>
          <g transform="translate(225, 225)">
            <path d="M-10 -10 Q-10 -20 0 -20 Q10 -20 10 -10 Q10 0 0 10 Q-10 0 -10 -10"/>
          </g>
          <g transform="translate(390, 225)">
            <circle cx="0" cy="0" r="25"/>
            <path d="M-15 12 L-22 22"/>
          </g>
          
          <!-- Row 3 -->
          <g transform="translate(60, 390)">
            <rect x="-20" y="-12" width="40" height="24" rx="2"/>
            <path d="M-20 -12 L0 0 L20 -12"/>
          </g>
          <g transform="translate(225, 390)">
            <circle cx="0" cy="0" r="25"/>
            <path d="M15 12 L22 22"/>
          </g>
          <g transform="translate(390, 390)">
            <rect x="-15" y="-20" width="30" height="40" rx="4"/>
            <line x1="-10" y1="-15" x2="10" y2="-15"/>
            <circle cx="0" cy="15" r="2.5"/>
          </g>
        </g>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const generateWhatsAppGreenPattern = () => {
    const svg = `
      <svg width="450" height="450" xmlns="http://www.w3.org/2000/svg">
        <rect width="450" height="450" fill="#dcf8c6"/>
        <g opacity="0.05" fill="none" stroke="#075e54" stroke-width="1.2" stroke-linecap="round">
          <!-- Row 1 -->
          <g transform="translate(60, 60)">
            <circle cx="0" cy="0" r="25"/>
            <path d="M-15 12 L-22 22"/>
          </g>
          <g transform="translate(225, 60)">
            <rect x="-15" y="-20" width="30" height="40" rx="4"/>
            <line x1="-10" y1="-15" x2="10" y2="-15"/>
            <circle cx="0" cy="15" r="2.5"/>
          </g>
          <g transform="translate(390, 60)">
            <rect x="-20" y="-12" width="40" height="24" rx="2"/>
            <path d="M-20 -12 L0 0 L20 -12"/>
          </g>
          
          <!-- Row 2 -->
          <g transform="translate(60, 225)">
            <path d="M0 -15 Q-12 -20 -15 -10 Q-18 0 -10 8 Q0 15 0 15"/>
            <circle cx="-10" cy="-5" r="4"/>
          </g>
          <g transform="translate(225, 225)">
            <path d="M-10 -10 Q-10 -20 0 -20 Q10 -20 10 -10 Q10 0 0 10 Q-10 0 -10 -10"/>
          </g>
          <g transform="translate(390, 225)">
            <circle cx="0" cy="0" r="25"/>
            <path d="M-15 12 L-22 22"/>
          </g>
          
          <!-- Row 3 -->
          <g transform="translate(60, 390)">
            <rect x="-20" y="-12" width="40" height="24" rx="2"/>
            <path d="M-20 -12 L0 0 L20 -12"/>
          </g>
          <g transform="translate(225, 390)">
            <circle cx="0" cy="0" r="25"/>
            <path d="M15 12 L22 22"/>
          </g>
          <g transform="translate(390, 390)">
            <rect x="-15" y="-20" width="30" height="40" rx="4"/>
            <line x1="-10" y1="-15" x2="10" y2="-15"/>
            <circle cx="0" cy="15" r="2.5"/>
          </g>
        </g>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  // Predefined wallpapers
  const predefinedWallpapers = [
    // Default wallpaper option
    { 
      id: 'default', 
      name: 'Default', 
      url: '#ffffff',
      isDefault: true
    },
    // Chat Icon Patterns
    { 
      id: 'chat-pattern-teal', 
      name: 'Chat Icons - Teal', 
      url: generateChatPatternSVG('#103937', '#7fb1a8'),
      isPattern: true 
    },
    
    // Canvas Effect Patterns - Blue
    { 
      id: 'canvas-particles-blue', 
      name: '🔗 Particles Network - Blue', 
      url: generateCanvasParticlesNetwork('blue'),
      isPattern: true 
    },
    { 
      id: 'canvas-constellation-blue', 
      name: '⭐ Constellation - Blue', 
      url: generateCanvasConstellation('blue'),
      isPattern: true 
    },
    { 
      id: 'canvas-hexagons-blue', 
      name: '⬡ Hexagon Grid - Blue', 
      url: generateCanvasHexagons('blue'),
      isPattern: true 
    },

    { 
      id: 'canvas-neon-grid-blue', 
      name: '🌃 Neon Grid - Blue', 
      url: generateCanvasNeonGrid('blue'),
      isPattern: true 
    },
    
    // Canvas Effect Patterns - Red
    { 
      id: 'canvas-particles-red', 
      name: '🔗 Particles Network - Red', 
      url: generateCanvasParticlesNetwork('red'),
      isPattern: true 
    },

    { 
      id: 'canvas-hexagons-red', 
      name: '⬡ Hexagon Grid - Red', 
      url: generateCanvasHexagons('red'),
      isPattern: true 
    },

    { 
      id: 'canvas-neon-grid-red', 
      name: '🌃 Neon Grid - Red', 
      url: generateCanvasNeonGrid('red'),
      isPattern: true 
    },
    
    // Canvas Effect Patterns - White
    { 
      id: 'canvas-particles-white', 
      name: '🔗 Particles Network - White', 
      url: generateCanvasParticlesNetwork('white'),
      isPattern: true 
    },

    { 
      id: 'canvas-hexagons-white', 
      name: '⬡ Hexagon Grid - White', 
      url: generateCanvasHexagons('white'),
      isPattern: true 
    },

    { 
      id: 'canvas-neon-grid-white', 
      name: '🌃 Neon Grid - White', 
      url: generateCanvasNeonGrid('white'),
      isPattern: true 
    },
    
    // Light Beam Effects
    { 
      id: 'spotlight-white', 
      name: '🔦 Spotlight - White', 
      url: generateSpotlightSVG('white'),
      isPattern: true 
    },
    { 
      id: 'aurora', 
      name: '🌌 Aurora Lights', 
      url: generateAuroraSVG(),
      isPattern: true 
    },
    
    // Pulsing Ring Effects
    { 
      id: 'pulse-rings-pink', 
      name: '⭕ Pulse Rings - Pink', 
      url: generatePulsingRingsSVG('pink'),
      isPattern: true 
    },
    
    // Animated Dark Patterns
    { 
      id: 'animated-stars', 
      name: '✨ Animated Stars', 
      url: generateAnimatedStarsSVG(),
      isPattern: true 
    },
    { 
      id: 'animated-particles', 
      name: '🌌 Floating Particles', 
      url: generateAnimatedParticlesSVG(),
      isPattern: true 
    },
    { 
      id: 'animated-glow', 
      name: '💫 Glowing Orbs', 
      url: generateAnimatedGlowSVG(),
      isPattern: true 
    },
    { 
      id: 'animated-waves', 
      name: '🌊 Animated Waves', 
      url: generateAnimatedWavesSVG(),
      isPattern: true 
    },
    { 
      id: 'animated-matrix', 
      name: '💻 Matrix Rain', 
      url: generateAnimatedMatrixSVG(),
      isPattern: true 
    },
    
    // Bubbles Patterns
    { 
      id: 'bubbles-teal', 
      name: 'Bubbles - Teal', 
      url: generateBubblesPatternSVG('#103937', '#7fb1a8'),
      isPattern: true 
    }
  ];

  const handleWallpaperSelect = (wallpaper) => {
    setSelectedWallpaper(wallpaper.id);
    // For pattern wallpapers, use repeat settings for SVG patterns
    const defaultSettings = wallpaper.isPattern ? {
      backgroundSize: 'auto',
      backgroundRepeat: 'repeat',
      backgroundPosition: 'center'
    } : displaySettings;
    
    onSelectWallpaper({
      ...wallpaper,
      displaySettings: defaultSettings
    });
  };

  // Apply settings changes immediately
  const handleDisplaySettingsChange = (newSettings) => {
    setDisplaySettings(newSettings);
    // Apply changes immediately if a wallpaper is selected
    if (selectedWallpaper) {
      const wallpaper = selectedWallpaper === 'custom' 
        ? currentWallpaper 
        : predefinedWallpapers.find(w => w.id === selectedWallpaper);
      
      if (wallpaper) {
        onSelectWallpaper({
          ...wallpaper,
          displaySettings: newSettings
        });
      }
    }
  };

  const handleGallerySelect = () => {
    fileInputRef.current.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      // Check file size (limit to 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('Image size should be less than 2MB. Please choose a smaller image.');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        // Compress the image before storing
        compressImage(e.target.result, (compressedDataUrl) => {
          const customWallpaper = {
            id: 'custom',
            name: 'Custom',
            url: compressedDataUrl,
            isCustom: true,
            displaySettings: displaySettings
          };
          setSelectedWallpaper('custom');
          onSelectWallpaper(customWallpaper);
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Image compression function
  const compressImage = (dataUrl, callback) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set maximum dimensions
      const maxWidth = 800;
      const maxHeight = 600;
      
      // Calculate new dimensions
      let { width, height } = img;
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to compressed data URL (quality 0.7)
      const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
      callback(compressedDataUrl);
    };
    img.src = dataUrl;
  };

  const handleApply = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="wallpaper-picker-overlay" onClick={onClose}>
      <div className="wallpaper-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wallpaper-picker-header">
          <h3>Choose Wallpaper</h3>
          <button className="close-button" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18"/>
              <path d="M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="wallpaper-picker-content">
          <div className="wallpaper-section">
            <h4>Predefined Themes</h4>
            <div className="wallpaper-grid">
              {predefinedWallpapers.map((wallpaper) => (
                <div
                  key={wallpaper.id}
                  className={`wallpaper-option ${selectedWallpaper === wallpaper.id ? 'selected' : ''}`}
                  onClick={() => handleWallpaperSelect(wallpaper)}
                >
                  <div 
                    className="wallpaper-preview"
                    style={{ 
                      background: wallpaper.isDefault 
                        ? wallpaper.url 
                        : wallpaper.isPattern 
                          ? `url(${wallpaper.url})` 
                          : wallpaper.url,
                      backgroundSize: wallpaper.isPattern ? 'cover' : 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {wallpaper.isDefault && (
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#999' }}>
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                      </svg>
                    )}
                  </div>
                  <span className="wallpaper-name">{wallpaper.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="wallpaper-section">
            <h4>Custom Wallpaper</h4>
            <div className="gallery-option" onClick={handleGallerySelect}>
              <div className="gallery-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21,15 16,10 5,21"/>
                </svg>
              </div>
              <span>Choose from Gallery</span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {/* Display Options - Only show for custom wallpapers */}
          {selectedWallpaper === 'custom' && (
            <div className="wallpaper-section">
              <h4>Display Options</h4>
              
              {/* Preview */}
              <div className="preview-section">
                <label>Preview:</label>
                <div 
                  className="preview-box"
                  style={{
                    background: currentWallpaper?.isCustom 
                      ? `url(${currentWallpaper.url})` 
                      : '#f0f0f0',
                    backgroundSize: displaySettings.backgroundSize,
                    backgroundPosition: displaySettings.backgroundPosition,
                    backgroundRepeat: displaySettings.backgroundRepeat
                  }}
                >
                  <div className="preview-content">Live Preview</div>
                </div>
              </div>
              
              <div className="display-options">
                <div className="option-group">
                  <label>Background Size:</label>
                  <select 
                    value={displaySettings.backgroundSize} 
                    onChange={(e) => handleDisplaySettingsChange({...displaySettings, backgroundSize: e.target.value})}
                  >
                    <option value="cover">Cover (Scale to fill)</option>
                    <option value="contain">Contain (Fit entire image)</option>
                    <option value="100% 100%">Stretch (Fill exactly)</option>
                    <option value="auto">Original Size</option>
                  </select>
                </div>
                
                <div className="option-group">
                  <label>Background Repeat:</label>
                  <select 
                    value={displaySettings.backgroundRepeat} 
                    onChange={(e) => handleDisplaySettingsChange({...displaySettings, backgroundRepeat: e.target.value})}
                  >
                    <option value="no-repeat">No Repeat</option>
                    <option value="repeat">Repeat (Tile)</option>
                    <option value="repeat-x">Repeat Horizontally</option>
                    <option value="repeat-y">Repeat Vertically</option>
                  </select>
                </div>
                
                <div className="option-group">
                  <label>Background Position:</label>
                  <select 
                    value={displaySettings.backgroundPosition} 
                    onChange={(e) => handleDisplaySettingsChange({...displaySettings, backgroundPosition: e.target.value})}
                  >
                    <option value="center">Center</option>
                    <option value="top">Top</option>
                    <option value="bottom">Bottom</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                    <option value="top left">Top Left</option>
                    <option value="top right">Top Right</option>
                    <option value="bottom left">Bottom Left</option>
                    <option value="bottom right">Bottom Right</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="wallpaper-picker-footer">
          <button className="apply-button" onClick={handleApply}>
            Apply Wallpaper
          </button>
        </div>
      </div>
    </div>
  );
};

export default WallpaperPicker;
